import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToTarget, setScrollLock } from "@/lib/scroll";
import { EASE } from "./Motion";

export const NAV = [
  { id: "story", label: "Story So Far" },
  { id: "brands", label: "Brands" },
  { id: "news", label: "News" },
  { id: "appointment", label: "Appointment" },
  { id: "contact", label: "Contact" },
];

export const useSectionNav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (id) => {
    if (pathname === "/") scrollToTarget(`#${id}`);
    else navigate(`/#${id}`);
  };
};

const MobileMenu = ({ open, onGo }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="mobile-menu"
        className="fixed inset-0 z-40 flex flex-col justify-end bg-[#0a0b0d] px-6 pb-12 pt-28 lg:hidden"
        initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        exit={{ clipPath: "inset(0% 0% 100% 0%)" }}
        transition={{ duration: 0.7, ease: EASE }}
      >
        {NAV.map((n, i) => (
          <motion.button
            key={n.id}
            data-testid={`mobile-nav-${n.id}`}
            onClick={() => onGo(n.id)}
            className="flex items-baseline gap-4 border-b border-white/10 py-4 text-left font-display text-5xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 + i * 0.06, duration: 0.7, ease: EASE }}
          >
            <span className="font-mono text-xs text-[#c8a97e]">0{i + 1}</span>
            {n.label}
          </motion.button>
        ))}
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">Dundrum · Dublin 14 · +353 1 207 9300</p>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const go = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setScrollLock(open);
  }, [open]);

  const handleGo = (id) => {
    setOpen(false);
    setTimeout(() => go(id), open ? 350 : 0);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled || open ? "border-b border-white/10 bg-[#0a0b0d]/75 py-4 backdrop-blur-xl" : "border-b border-transparent py-6"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 sm:px-10 lg:px-14">
          <Link to="/" data-testid="header-logo-link" onClick={() => { setOpen(false); scrollToTarget(0); }}>
            <img data-testid="header-logo" src="/assets/jh-sun-logo.png" alt="Jaisun House" className="h-11 w-auto transition-transform duration-500 hover:scale-105 sm:h-12" />
          </Link>
          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)} className="link-u pb-1 text-[13px] uppercase tracking-[0.18em] text-[#d6d3cd] transition-colors hover:text-white">
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="header-book-btn"
              onClick={() => handleGo("appointment")}
              className="group hidden items-center gap-2 rounded-full border border-[#c8a97e]/50 px-5 py-2.5 text-[12px] uppercase tracking-[0.18em] text-[#c8a97e] transition-colors duration-300 hover:bg-[#c8a97e] hover:text-[#0a0b0d] sm:flex"
            >
              Book Showroom <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setOpen((o) => !o)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 lg:hidden">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={open} onGo={handleGo} />
    </>
  );
};
